import { isVideoUrl } from '../../../tokens/mediaFormatUtils';
import { isImageEdit } from '../../../tools';
import { type FileEditCompleteImageEdit, type FileEditCompleteResult } from '../../../tools/fileEditComplete';

export type SmartResizeFollowUpMediaType = 'image' | 'video';

/**
 * Scope of a smart-resize result (a composited group counts as one variant).
 * Only smart-resize runs stamp `smartResizeFormatId` on their image edits, so a result
 * without any yields null.
 *
 * `mediaType` comes from the edit URLs rather than the edit shape — `file_edit_complete`
 * delivers reframed videos through the same image-shaped result as images.
 */
export const getSmartResizeFollowUpScope = (
  result: FileEditCompleteResult,
): { formatIds: string[]; mediaType: SmartResizeFollowUpMediaType; runId: string; variantCount: number } | null => {
  const imageEdits = result.edits.filter(isImageEdit);
  const taggedEdits = imageEdits.filter((edit): edit is FileEditCompleteImageEdit & { smartResizeFormatId: string } =>
    Boolean(edit.smartResizeFormatId),
  );

  if (taggedEdits.length === 0) {
    return null;
  }

  // A composited group's layers all carry the same format id, so only the group's first tagged edit counts toward a variant.
  const formatIds: string[] = [];
  const seenGroupIds = new Set<string>();

  for (const edit of taggedEdits) {
    const groupId = edit.group?.id;

    if (groupId) {
      if (seenGroupIds.has(groupId)) {
        continue;
      }
      seenGroupIds.add(groupId);
    }

    formatIds.push(edit.smartResizeFormatId);
  }

  return {
    formatIds,
    mediaType: imageEdits.every((edit) => isVideoUrl(edit.url)) ? 'video' : 'image',
    runId: result.prompt.runId,
    variantCount: formatIds.length,
  };
};
